import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Package, Clock, Truck, CheckCircle, AlertCircle } from 'lucide-react';
import { getOrders, Order } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

function StatusBadge({ status }: { status: string }) {
  const badgeClass = {
    PENDING: 'badge-pending',
    READY: 'badge-ready',
    QUOTE_SENT: 'badge-ready',
    QUOTE_APPROVED: 'badge-in-delivery',
    IN_DELIVERY: 'badge-in-delivery',
    DELIVERED: 'badge-delivered',
    CANCELLED: 'badge-cancelled',
    FAILED: 'badge-failed',
  }[status] || 'badge-pending';

  return <span className={`badge ${badgeClass}`}>{status.replace(/_/g, ' ')}</span>;
}

function StatCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}) {
  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['orders'],
    queryFn: () => getOrders(),
  });

  const stats = {
    pending: orders.filter((o: Order) => o.status === 'PENDING').length,
    awaitingApproval: orders.filter((o: Order) => o.status === 'QUOTE_SENT').length,
    inDelivery: orders.filter((o: Order) => o.status === 'IN_DELIVERY').length,
    delivered: orders.filter((o: Order) => o.status === 'DELIVERED').length,
  };

  const recentOrders = orders.slice(0, 5);

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uber-green"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold mb-6">Dashboard</h2>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Pending Production"
          value={stats.pending}
          icon={Package}
          color="bg-yellow-500"
        />
        <StatCard
          title="Awaiting Approval"
          value={stats.awaitingApproval}
          icon={Clock}
          color="bg-blue-500"
        />
        <StatCard
          title="In Delivery"
          value={stats.inDelivery}
          icon={Truck}
          color="bg-purple-500"
        />
        <StatCard
          title="Delivered Today"
          value={stats.delivered}
          icon={CheckCircle}
          color="bg-green-500"
        />
      </div>

      {/* Recent Orders */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Recent Orders</h3>
          <Link to="/orders" className="text-uber-green hover:underline text-sm">
            View all
          </Link>
        </div>

        {recentOrders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No orders yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 border-b">
                  <th className="pb-3 font-medium">Order</th>
                  <th className="pb-3 font-medium">Customer</th>
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium">Created</th>
                  <th className="pb-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order: Order) => (
                  <tr key={order.id} className="border-b last:border-0">
                    <td className="py-4 font-medium">{order.shopifyOrderNumber}</td>
                    <td className="py-4">{order.customerName}</td>
                    <td className="py-4">
                      <StatusBadge status={order.status} />
                    </td>
                    <td className="py-4 text-gray-500">
                      {formatDistanceToNow(new Date(order.createdAt), {
                        addSuffix: true,
                      })}
                    </td>
                    <td className="py-4">
                      <Link
                        to={`/orders/${order.id}`}
                        className="text-uber-green hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Alerts */}
      {stats.awaitingApproval > 0 && (
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <p className="text-yellow-800">
            <strong>{stats.awaitingApproval}</strong> order(s) awaiting customer approval.
            Quotes may expire soon!
          </p>
        </div>
      )}
    </div>
  );
}
